import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { logout } from "../../../../LoginPage/redux/auth.actions"
import {
  activeProcessorOf,
  fillBlankAnswers,
  normalizeBuildingBlocks,
  readSection,
  SCHEMA_SECTIONS,
  writeSection
} from "./buildingBlocks"
import { DISPUTES_CONFIGURE_SLICE_NAME as SLICE_NAME } from "./configure.constants"
import {
  activateRecover,
  commitSection,
  connectTool,
  deactivateRecover,
  finishSetup,
  getEnrichmentTools,
  getIntegrationCatalog,
  getProfile,
  getShopifyScopeStatus,
  prefillFromWebsite,
  refreshPrefill,
  removeVisualEvidence,
  requestIntegration,
  savePreferences,
  uploadVisualEvidence
} from "./configure.thunks"

/* ------------- Initial State ------------- */

const initialState = {
  profile: {
    id: null,
    isActivated: false,
    // Whether Recover was EVER activated, straight from the backend
    // (RecoverProfile.first_activated_at). Distinguishes a merchant who set Recover up
    // and later switched it off from one who never finished setup — they get different
    // banners. Used to be a localStorage marker written by the front end.
    everActivated: false,
    connectedProcessors: [],
    // Which connected processors activation currently covers, and how far each one's
    // setup has got — both answered by the account rather than by this browser, so a
    // merchant who activated on a laptop sees the same coverage on their phone.
    activatedProcessors: [],
    setup: {},
    disputesCount: 0,
    activatedAt: null,
    // Backend `updated` timestamp (auto_now). Surfaced so the Configure rail can
    // show "Updated <date>" on the finished-profile card.
    lastUpdated: null,
    buildingBlocks: {},
    // When several payment processors are connected, steps 2-6 are filled per
    // processor. This is the slug whose steps are currently shown/edited; the
    // per-processor answers live under buildingBlocks.byProcessor[slug]. Null /
    // single-processor merchants behave exactly as before.
    activeProcessor: null,
    // Scope state of the connected Shopify shop. `needsUpgrade` means the shop is
    // connected but was authorized before Recover asked for the dispute-evidence
    // scopes; `reauthUrl` upgrades the existing installation in place. Null until
    // the check has answered, so the card shows no verdict rather than a wrong one.
    shopifyScope: {
      connected: false,
      needsUpgrade: false,
      missingScopes: [],
      reauthUrl: null,
      checked: false
    },
    // Processor cards for the Integrate step, straight from the Recover backend. `loaded`
    // stays false until the first answer so the cards fall back to the Prevent list's data
    // rather than briefly reading as disconnected.
    integrationCatalog: { processors: [], connectedCount: 0, loaded: false },
    // Enrichment tool cards, straight from the Recover backend. Connections are per business
    // account, so the rail and the summary read every processor's enrichment from here.
    enrichmentCatalog: { tools: [], loaded: false },
    preferences: { auto_approve_all: false, notify_email: true, notify_slack: false },
    sectionCompletion: {
      required_information: 0,
      general_information: 0,
      checkout_policies: 0,
      verifications: 0,
      evidence_vault: 0
    },
    totalOptimizationScore: 0,
    // Per processor: { collecting, collected_at, groups, filled } from the prefill collector.
    prefill: {},
    // A refreshPrefill request is in flight, so the poll never stacks a second one.
    prefillRefreshing: false,
    // True when the merchant has a connected Slack channel via the existing
    // slack_app.AlertSlackChannel binding. Drives the Slack toggle in the
    // Recover preferences page. without a channel the toggle stays disabled
    // and links to the integrations page.
    hasSlackChannel: false,
    fetching: false,
    // False until the first profile fetch resolves, so the route shell waits
    // before deciding activated-vs-not (avoids a flash/redirect on direct loads).
    profileLoaded: false,
    // True when the LAST profile fetch errored (5xx/401/timeout, i.e. not a clean
    // backend-unavailable LS-fallback). Lets the shell tell a load failure apart
    // from a genuinely deactivated profile, so it doesn't show the alarming
    // "Recover is deactivated" banner on a transient/recoverable error.
    loadFailed: false,
    error: null,
    // Id of the in-flight profile fetch. The saga used takeLatest; thunks aren't
    // cancelled, so a superseded answer is dropped by comparing this instead.
    requestId: null
  },
  // Enrichment connect in flight (Intercom). `provider` names the card the error belongs to.
  toolConnect: { loading: false, error: null, provider: null },
  // "Integration missing?" request. `done` flips the modal to its success view.
  integrationRequest: { loading: false, done: false, error: null },
  ui: {
    currentSection: "required_information",
    autoSaveStatus: "idle", // idle | saving | saved | error
    // { site, filled: [], skipped: [], errors: [] }. populated after the
    // website scrape on Step 1 commit, consumed by the Step 2 autofill banner.
    prefillSummary: null,
    prefillRunning: false,
    // URL of the last successful website scrape, so Step 1 can skip re-scraping an
    // unchanged URL without depending on a backend-written field.
    lastScrapedSite: null,
    // Sections with unsaved edits, for the beforeunload guard. Read with `|| {}`, but
    // declared here for state-shape completeness.
    dirtySections: {},
    // Id of the newest section commit. The Footer fires one commit per section of a
    // multi-section step in a single tick, and the saga's takeLatest meant only the
    // last one reported back — every request still went out, but the save status and
    // the completion figures came from that last answer alone. Thunks aren't
    // cancelled, so the same "last one wins" is enforced by comparing this.
    commitRequestId: null
  },
  // Snapshot of preferences taken before an optimistic save, so a failed save can
  // roll back instead of silently keeping a value the backend rejected.
  preferencesBackup: null
}

/* ------------- Shared handlers ------------- */

// Writing a fetched profile onto the slice. The finish-setup endpoint answers with the
// whole refreshed payload, so it takes the same route the initial fetch does.
const applyProfile = (state, profile) => {
  const p = profile || {}
  const prefs = p.preferences ?? {}
  const buildingBlocks = normalizeBuildingBlocks(p.building_blocks)
  const connected = buildingBlocks.processors?.connected || []
  // Keep a valid active processor across reloads: reuse the current one if it's
  // still connected, else default to the first connected processor.
  const activeProcessor =
    state.profile.activeProcessor && connected.includes(state.profile.activeProcessor)
      ? state.profile.activeProcessor
      : connected[0] || null

  state.profile.id = p.id ?? null
  state.profile.isActivated = !!p.is_activated
  state.profile.everActivated = !!p.ever_activated
  state.profile.connectedProcessors = p.connected_processors ?? []
  state.profile.activatedProcessors = p.activated_processors ?? []
  state.profile.setup = p.setup ?? {}
  state.profile.disputesCount = p.disputes_count ?? 0
  state.profile.activatedAt = p.activated_at ?? null
  state.profile.lastUpdated = p.updated ?? null
  state.profile.buildingBlocks = buildingBlocks
  state.profile.activeProcessor = activeProcessor
  state.profile.preferences = { ...state.profile.preferences, ...prefs }
  state.profile.sectionCompletion = {
    ...state.profile.sectionCompletion,
    ...(p.section_completion ?? {})
  }
  state.profile.totalOptimizationScore = p.total_optimization_score ?? 0
  state.profile.prefill = p.prefill ?? {}
  // The profile is persisted, so a reload in the middle of a poll would otherwise leave this
  // stuck on and block every later one.
  state.profile.prefillRefreshing = false
  state.profile.hasSlackChannel = !!p.has_slack_channel
  state.profile.fetching = false
  state.profile.profileLoaded = true
  state.profile.loadFailed = false
  state.profile.error = null
}

/* ------------- Slice ------------- */

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    // Field changes are Redux-only, no save status indicator: the reducer merges the
    // patch and the debounced auto-commit listener PATCHes it 800ms later.
    saveSection(state, { payload }) {
      const section = payload?.section
      if (!section) return
      const patch = payload?.patch ?? {}
      const prevSection = readSection(state.profile, section)
      state.profile.buildingBlocks = writeSection(state.profile, section, {
        ...prevSection,
        ...patch
      })
      state.ui.dirtySections = { ...(state.ui.dirtySections || {}), [section]: true }
    },
    clearPrefillSummary(state) {
      state.ui.prefillSummary = null
    },
    requestIntegrationReset(state) {
      state.integrationRequest = { loading: false, done: false, error: null }
    },
    setCurrentSection(state, { payload }) {
      state.ui.currentSection = payload
    },
    setActiveProcessor(state, { payload }) {
      state.profile.activeProcessor = payload || null
    },
    setAutoSaveStatus(state, { payload }) {
      state.ui.autoSaveStatus = payload
    }
  },
  extraReducers: (builder) => {
    builder
      // Signing out clears the slice, via the shared LOGOUT action every other slice
      // resets on (LoginPage/redux/auth.actions).
      .addCase(logout, () => initialState)
      // getProfile
      .addCase(getProfile.pending, (state, { meta }) => {
        state.profile.fetching = true
        state.profile.error = null
        state.profile.loadFailed = false
        state.profile.requestId = meta.requestId
      })
      .addCase(getProfile.fulfilled, (state, { payload, meta }) => {
        if (state.profile.requestId !== meta.requestId) return
        state.profile.requestId = null
        applyProfile(state, payload?.profile)
      })
      .addCase(getProfile.rejected, (state, { payload, meta }) => {
        if (state.profile.requestId !== meta.requestId) return
        state.profile.requestId = null
        state.profile.fetching = false
        state.profile.profileLoaded = true
        state.profile.loadFailed = true
        state.profile.error = payload ?? null
      })

      // refreshPrefill — the poll while the collector runs. Takes the readings into blank
      // questions only; a full applyProfile here would throw away unsaved edits.
      .addCase(refreshPrefill.pending, (state) => {
        state.profile.prefillRefreshing = true
      })
      .addCase(refreshPrefill.fulfilled, (state, { payload }) => {
        const fetched = payload?.profile
        state.profile.prefillRefreshing = false
        state.profile.prefill = fetched?.prefill ?? {}
        state.profile.buildingBlocks = fillBlankAnswers(
          state.profile.buildingBlocks,
          normalizeBuildingBlocks(fetched?.building_blocks)
        )
      })
      .addCase(refreshPrefill.rejected, (state) => {
        state.profile.prefillRefreshing = false
      })

      // commitSection — PATCHes the section to the backend, used by Save for later / Next.
      .addCase(commitSection.pending, (state, { meta }) => {
        state.ui.autoSaveStatus = "saving"
        state.ui.commitRequestId = meta.requestId
      })
      .addCase(commitSection.fulfilled, (state, { payload, meta }) => {
        if (state.ui.commitRequestId !== meta.requestId) return
        state.ui.commitRequestId = null
        const section = payload?.section
        const completion = payload?.section_completion ?? null
        const total = payload?.total_optimization_score ?? null
        const dirty = { ...(state.ui.dirtySections || {}) }
        if (section) delete dirty[section]
        if (completion !== null) {
          state.profile.sectionCompletion = {
            ...state.profile.sectionCompletion,
            ...completion
          }
        }
        if (total !== null) state.profile.totalOptimizationScore = total
        state.ui.autoSaveStatus = "saved"
        state.ui.dirtySections = dirty
      })
      .addCase(commitSection.rejected, (state, { meta }) => {
        if (state.ui.commitRequestId !== meta.requestId) return
        state.ui.commitRequestId = null
        state.ui.autoSaveStatus = "error"
      })

      // finishSetup — the endpoint answers with the whole refreshed payload, so the
      // profile is replaced from it rather than patched.
      .addCase(finishSetup.pending, (state) => {
        state.profile.fetching = true
        state.profile.error = null
      })
      .addCase(finishSetup.fulfilled, (state, { payload }) => {
        applyProfile(state, payload?.profile)
      })
      .addCase(finishSetup.rejected, (state, { payload }) => {
        state.profile.fetching = false
        state.profile.error = payload ?? null
      })

      // savePreferences — optimistic on pending, rolled back to the snapshot on rejected.
      .addCase(savePreferences.pending, (state, { meta }) => {
        // Keep the pre-patch preferences so a rejection can roll the optimistic update back.
        state.preferencesBackup = state.profile.preferences
        state.profile.preferences = {
          ...state.profile.preferences,
          ...(meta.arg?.patch ?? {})
        }
      })
      .addCase(savePreferences.fulfilled, (state) => {
        state.preferencesBackup = null
      })
      .addCase(savePreferences.rejected, (state) => {
        if (state.preferencesBackup == null) return
        state.profile.preferences = state.preferencesBackup
        state.preferencesBackup = null
      })

      // uploadVisualEvidence
      .addCase(uploadVisualEvidence.pending, (state) => {
        state.ui.autoSaveStatus = "saving"
      })
      .addCase(uploadVisualEvidence.fulfilled, (state, { payload }) => {
        const section = payload?.section || "evidence_vault"
        const { slot, s3Key, url, fileName } = payload ?? {}
        const prev = readSection(state.profile, section)
        state.profile.buildingBlocks = writeSection(state.profile, section, {
          ...prev,
          [slot]: { s3_key: s3Key, url, file_name: fileName }
        })
        state.ui.autoSaveStatus = "saved"
      })
      .addCase(uploadVisualEvidence.rejected, (state) => {
        state.ui.autoSaveStatus = "error"
      })

      .addCase(removeVisualEvidence.pending, (state) => {
        state.ui.autoSaveStatus = "saving"
      })
      .addCase(removeVisualEvidence.fulfilled, (state, { payload }) => {
        const { section, slot } = payload ?? {}
        const prev = readSection(state.profile, section)
        state.profile.buildingBlocks = writeSection(state.profile, section, {
          ...prev,
          [slot]: null
        })
        state.ui.autoSaveStatus = "saved"
      })
      .addCase(removeVisualEvidence.rejected, (state) => {
        state.ui.autoSaveStatus = "error"
      })

      // prefillFromWebsite
      .addCase(prefillFromWebsite.pending, (state) => {
        state.ui.prefillRunning = true
        state.ui.prefillSummary = null
      })
      .addCase(prefillFromWebsite.fulfilled, (state, { payload }) => {
        const summary = payload?.summary || null
        const merged = payload?.building_blocks || null
        // The prefill thunk commits required_information server-side before scraping but
        // never reports a section commit, so clear its dirty flag here to avoid a stale
        // unsaved-changes (beforeunload) warning once Step 1 is saved.
        const dirty = { ...(state.ui.dirtySections || {}) }
        delete dirty.required_information
        // The scrape returns a flat building_blocks; write its sections into the active
        // processor's bucket so a prefill fills the processor being configured, not a
        // shared blob.
        if (merged) {
          const active = activeProcessorOf(state.profile)
          const bb = state.profile.buildingBlocks || {}
          if (active) {
            const scraped = {}
            SCHEMA_SECTIONS.forEach((s) => {
              if (merged[s] !== undefined) scraped[s] = merged[s]
            })
            const byProcessor = { ...(bb.byProcessor || {}) }
            byProcessor[active] = { ...(byProcessor[active] || {}), ...scraped }
            state.profile.buildingBlocks = { ...bb, byProcessor }
          } else {
            state.profile.buildingBlocks = normalizeBuildingBlocks(merged)
          }
        }
        state.ui.prefillRunning = false
        state.ui.prefillSummary = summary
        state.ui.dirtySections = dirty
        if (payload?.website_url) state.ui.lastScrapedSite = payload.website_url
        // Then the refetched profile, exactly as the initial fetch applies it: this is
        // what refreshes the section completion + total score.
        if (payload?.profileData) applyProfile(state, payload.profileData)
      })
      .addCase(prefillFromWebsite.rejected, (state) => {
        state.ui.prefillRunning = false
        state.ui.prefillSummary = { ok: false, errors: ["Network error"] }
      })

      // activateRecover — activation answers with the gating payload, which names the
      // processors it now covers. Taking the list from the response rather than assuming
      // it keeps the browser out of a decision the account makes.
      .addCase(activateRecover.pending, (state) => {
        state.profile.fetching = true
        state.profile.error = null
      })
      .addCase(activateRecover.fulfilled, (state, { payload }) => {
        const p = payload?.profile ?? {}
        state.profile.isActivated = !!p.activated
        state.profile.everActivated = !!p.ever_activated
        state.profile.activatedProcessors =
          p.processors?.activated ?? state.profile.activatedProcessors
        state.profile.activatedAt = p.activated_at ?? new Date().toISOString()
        state.profile.fetching = false
        state.profile.error = null
      })
      .addCase(activateRecover.rejected, (state, { payload }) => {
        state.profile.fetching = false
        state.profile.error = payload ?? null
      })

      // deactivateRecover — deactivating one processor need not switch the account off:
      // `activated` stays true while any other processor is still covered.
      .addCase(deactivateRecover.fulfilled, (state, { payload }) => {
        const p = payload?.profile ?? {}
        const activatedProcessors = p.processors?.activated ?? []
        state.profile.isActivated = !!p.activated
        state.profile.activatedProcessors = activatedProcessors
        state.profile.activatedAt = activatedProcessors.length ? state.profile.activatedAt : null
      })

      // getShopifyScopeStatus — `checked` is what tells the card the verdict is in; a
      // failed check leaves it false so we say nothing rather than accuse a healthy shop
      // of missing scopes.
      .addCase(getShopifyScopeStatus.fulfilled, (state, { payload }) => {
        state.profile.shopifyScope = {
          connected: !!payload?.connected,
          needsUpgrade: !!payload?.needs_upgrade,
          missingScopes: payload?.missing_scopes || [],
          reauthUrl: payload?.reauth_url || null,
          checked: true
        }
      })

      // getIntegrationCatalog — a failed load keeps whatever was there, so a flaky
      // request cannot blank the cards mid-session.
      .addCase(getIntegrationCatalog.fulfilled, (state, { payload }) => {
        state.profile.integrationCatalog = {
          processors: Array.isArray(payload?.processors) ? payload.processors : [],
          connectedCount: payload?.connected_count || 0,
          loaded: true
        }
      })

      // requestIntegration
      .addCase(requestIntegration.pending, (state) => {
        state.integrationRequest = { loading: true, done: false, error: null }
      })
      .addCase(requestIntegration.fulfilled, (state) => {
        state.integrationRequest = { loading: false, done: true, error: null }
      })
      .addCase(requestIntegration.rejected, (state, { payload }) => {
        state.integrationRequest = {
          loading: false,
          done: false,
          error: payload || "Could not send the request."
        }
      })

      // connectTool — there is no fulfilled case on purpose: success navigates away to
      // the tool's authorize page, so `loading` stays on until the browser leaves.
      .addCase(connectTool.pending, (state, { meta }) => {
        state.toolConnect = { loading: true, error: null, provider: meta.arg?.slug || null }
      })
      .addCase(connectTool.rejected, (state, { payload, meta }) => {
        state.toolConnect = {
          loading: false,
          error: payload || "Could not connect.",
          provider: meta.arg?.slug || null
        }
      })

      // getEnrichmentTools — like the processor catalog, a failed load keeps the last cards.
      .addCase(getEnrichmentTools.fulfilled, (state, { payload }) => {
        state.profile.enrichmentCatalog = {
          tools: Array.isArray(payload?.tools) ? payload.tools : [],
          loaded: true
        }
      })
  }
})

export const {
  saveSection,
  clearPrefillSummary,
  requestIntegrationReset,
  setCurrentSection,
  setActiveProcessor,
  setAutoSaveStatus
} = slice.actions

/* ------------- Selectors ------------- */
// Defined in ./buildingBlocks (a leaf module the thunks can read without importing this
// slice) and re-exported here so components import selectors from the slice as usual.
export {
  selectActiveBuildingBlocks,
  selectActiveProcessor,
  selectActivatedProcessors,
  selectBuildingBlocks,
  selectEnrichmentCatalog,
  selectConnectedProcessors,
  selectEverActivated,
  selectFinishedProcessors,
  selectIsActivated,
  selectPrefillCollecting,
  selectProfile,
  selectSetupState,
  selectUi
} from "./buildingBlocks"

/* ------------- Persistence ------------- */
// `profile` and `ui` stay persisted: the active processor and the step the merchant is
// on have to survive a reload mid-wizard. The transient request state below does not.

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "disputesConfigureThunk",
    storage,
    version: PERSISTED_REDUCER_VERSION,
    blacklist: ["toolConnect", "integrationRequest", "preferencesBackup"],
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
